<template>
  <div
    class="shopify-carousel"
    :class="{
      'shopify-carousel--ready': ready,
      'shopify-carousel--locked': locked,
      'shopify-carousel--show-prev': showPrev,
    }"
    v-show="!failed"
  >
    <div class="swiper shopify-carousel__swiper" ref="swiper">
      <div class="swiper-wrapper">
        <div
          v-for="product in products"
          :key="product.id"
          class="swiper-slide shopify-carousel__slide"
        >
          <div class="shopify-carousel__product" :data-product-id="product.numericId"></div>
        </div>
      </div>
    </div>

    <div class="ms-arrows shopify-carousel__arrows">
      <button type="button" class="ms-arrow ms-left" aria-label="Previous products">
        <i v-icon:arrow-left-alt></i>
      </button>
      <button type="button" class="ms-arrow ms-right" aria-label="Next products">
        <i v-icon:arrow-right-alt></i>
      </button>
    </div>

    <div class="shopify-carousel__dots" v-show="!locked">
      <button
        v-for="(product, index) in products"
        :key="'dot-' + product.id"
        type="button"
        class="shopify-carousel__dot"
        :class="{ 'is-active': inView.indexOf(index) > -1 }"
        :aria-label="'Show product ' + (index + 1)"
        @click="goTo(index)"
      ></button>
    </div>
  </div>
</template>

<script>
import Swiper from "swiper";
import { Navigation } from "swiper/modules";
import "swiper/css";
import {
  DEFAULT_OPTIONS,
  collectionGid,
  getShop,
  numericId,
} from "../../utils/shopify";
import visibleProducts from "./visibleProducts";

export default {
  name: "ShopifyCarousel",
  props: {
    domain: { type: String, required: true },
    storefrontToken: { type: String, required: true },
    collectionId: { type: String, required: true },
    // Already URL-encoded, as in the Shopify embed code (no curly braces in the markup)
    moneyFormat: { type: String, default: "%24%7B%7Bamount%7D%7D" },
    // Optional replacement for the Buy Button options (styles and texts)
    options: { type: Object, default: null },
    // Swiper's own loop mode, under evaluation: moving a Shopify card may break its cart button
    loop: { type: Boolean, default: false },
  },
  data() {
    return {
      products: [],
      ready: false,
      failed: false,
      locked: false,
      moved: false,
      atStart: true,
      inView: [],
    };
  },
  computed: {
    // The previous arrow appears only after the visitor has moved right
    showPrev() {
      return this.moved && !this.atStart;
    },
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    this.destroyed = true;
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  },
  methods: {
    async load() {
      try {
        const { client, ui } = await getShop(this.domain, this.storefrontToken);
        const collection = await client.collection.fetchWithProducts(
          collectionGid(this.collectionId),
          { productsFirst: 50 }
        );
        if (this.destroyed) return;

        this.products = collection.products.map((product) => ({
          id: product.id,
          numericId: numericId(product.id),
        }));
        await this.$nextTick();

        this.initSwiper();
        await this.createProducts(ui);
        if (this.destroyed) return;

        this.swiper.update();
        this.sync();
        this.ready = true;
      } catch (error) {
        this.failed = true;
        console.warn("[shopify-carousel] could not render", error);
      }
    },
    initSwiper() {
      this.swiper = new Swiper(this.$refs.swiper, {
        modules: [Navigation],
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: 20,
        speed: 500,
        loop: this.loop,
        rewind: !this.loop,
        breakpoints: {
          768: { slidesPerView: 3 },
          1024: { slidesPerView: 4 },
          1280: { slidesPerView: 5 },
        },
        navigation: {
          nextEl: this.$el.querySelector(".ms-right"),
          prevEl: this.$el.querySelector(".ms-left"),
        },
      });
      ["slideChange", "breakpoint", "resize", "lock", "unlock"].forEach((name) =>
        this.swiper.on(name, this.sync)
      );
      this.swiper.on("slideNextTransitionStart", () => {
        this.moved = true;
      });
      this.sync();
    },
    sync() {
      const swiper = this.swiper;
      if (!swiper) return;
      const start = this.loop ? swiper.realIndex : swiper.activeIndex;
      this.inView = visibleProducts(
        start || 0,
        swiper.params.slidesPerView,
        this.products.length,
        this.loop
      );
      this.locked = !!swiper.isLocked;
      // activeIndex, not isBeginning: Swiper updates isBeginning after slideChange when going back
      this.atStart = !this.loop && swiper.activeIndex === 0;
    },
    goTo(index) {
      if (!this.swiper) return;
      if (this.loop) {
        this.swiper.slideToLoop(index);
      } else {
        this.swiper.slideTo(index);
      }
      if (index > 0) this.moved = true;
    },
    createProducts(ui) {
      const nodes = this.$el.querySelectorAll(".shopify-carousel__product");
      const options = this.options || DEFAULT_OPTIONS;
      return Promise.all(
        Array.prototype.map.call(nodes, (node) =>
          ui.createComponent("product", {
            id: node.getAttribute("data-product-id"),
            node: node,
            moneyFormat: this.moneyFormat,
            options: options,
          })
        )
      );
    },
  },
};
</script>
